import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { statuses } from '../../data/StatusColours'; // Adjust the import path
import { gameTypes } from '../../data/GameTypes'; // Adjust the import path
import { ArrowRightIcon, ArrowUpIcon } from '@heroicons/react/24/solid'; // Import new icons

// Add caching constants
const CAMPAIGN_STATS_CACHE_KEY = 'campaign_card_stats_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes for live campaigns
const COMPLETED_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours for completed campaigns

// Helper functions for caching
const getCachedData = (key, isCompleted = false) => {
  try {
    const cached = localStorage.getItem(key);
    if (cached) {
      const parsed = JSON.parse(cached);
      const cacheDuration = isCompleted ? COMPLETED_CACHE_DURATION : CACHE_DURATION;
      if (Date.now() - parsed.timestamp < cacheDuration) {
        return parsed.data;
      }
    }
  } catch (error) {
    console.error('Error reading cache:', error);
  }
  return null;
};

const setCachedData = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error setting cache:', error);
  }
};

// Helper function to render the comparison badge
function renderComparisonBadge(change) {
  if (change > 0) {
    return (
      <div className="ml-2 text-xs font-bold px-2 py-1 rounded-full flex items-center" style={{
        background: 'linear-gradient(135deg, #27AE60, #2ECC71)',
        border: '1px solid #F1C40F',
        color: '#FFFFFF',
        textShadow: '1px 1px 0px #1E8449'
      }}>
        <span className="mr-1">🔥</span> 
        {Math.abs(change).toLocaleString()}
      </div>
    );
  } else {
    return (
      <div className="ml-2 text-xs font-bold px-2 py-1 rounded-full" style={{
        background: 'rgba(52, 73, 94, 0.6)',
        border: '1px solid rgba(149, 165, 166, 0.3)',
        color: '#95A5A6'
      }}>
        -
      </div>
    );
  }
}

// Helper function to format the created date
function formatDate(date) {
  const options = { month: 'short', day: 'numeric' };
  return new Intl.DateTimeFormat('en-US', options).format(date);
}

function CampaignsCard({ campaign }) {
  const [playCount, setPlayCount] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  // Normalize date function from Stats.jsx
  const normalizeDate = (date) => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

  useEffect(() => {
    // Only fetch stats if campaign is live or complete
    if (campaign.status !== 'live' && campaign.status !== 'complete') {
      setIsLoading(false);
      return;
    }

    async function fetchStats() {
      // Check cache first
      const cacheKey = `${CAMPAIGN_STATS_CACHE_KEY}_${campaign.id}`;
      const cachedData = getCachedData(cacheKey, campaign.status === 'complete');
      
      if (cachedData) {
        setPlayCount(cachedData.playCount);
        setTodayCount(cachedData.todayCount);
        setIsLoading(false);
        return;
      }

      try {
        let totalPlayCountOverall = 0;
        let totalPlayCountToday = 0;
        
        const today = normalizeDate(new Date());
        const todayStart = new Date(today);
        todayStart.setHours(0, 0, 0, 0);
        const todayEnd = new Date(today);
        todayEnd.setDate(todayEnd.getDate() + 1);
        todayEnd.setHours(0, 0, 0, 0);
        
        // Get campaign IDs (same logic as in Stats.jsx)
        const campaignIds = campaign.gameIds || [campaign.id];
        
        // COST OPTIMIZATION: Sample recent entries instead of full aggregation
        for (const gameId of campaignIds) {
          const campaignEventRef = collection(db, 'campaignEvent');
          
          // Sample recent entries to estimate total (much cheaper)
          const recentQuery = query(
            campaignEventRef,
            where('game_id', '==', gameId),
            orderBy('timestamp', 'desc'),
            limit(100) // Only read 100 docs max per game
          );
          
          const recentSnapshot = await getDocs(recentQuery);
          const recentEntries = recentSnapshot.docs.map(doc => doc.data());
          
          if (recentEntries.length === 0) continue;
          
          // Count today's entries from sample
          const todayEntries = recentEntries.filter(entry => {
            const entryDate = entry.timestamp?.toDate() || new Date(entry.timestamp);
            return entryDate >= todayStart && entryDate < todayEnd;
          });
          
          // For overall stats, if we got a full 100 recent entries, estimate total
          if (recentEntries.length === 100) {
            // Estimate multiplier based on oldest entry in sample
            const oldestEntry = recentEntries[recentEntries.length - 1];
            const oldestDate = oldestEntry.timestamp?.toDate() || new Date(oldestEntry.timestamp);
            const daysInSample = Math.max(1, (Date.now() - oldestDate.getTime()) / (1000 * 60 * 60 * 24));
            
            // Conservative estimate: assume linear growth
            const estimateMultiplier = Math.min(10, Math.max(1, 30 / daysInSample)); // Cap at 10x
            totalPlayCountOverall += Math.round(recentEntries.length * estimateMultiplier);
          } else {
            // If less than 100 entries, use actual count
            totalPlayCountOverall += recentEntries.length;
          }
          
          // Add today's actual count (only if campaign is still live)
          if (campaign.status !== 'complete') {
            totalPlayCountToday += todayEntries.length;
          }
        }
        
        setPlayCount(totalPlayCountOverall);
        setTodayCount(totalPlayCountToday);
        
        // Cache the results with different strategy for completed campaigns
        const statsToCache = {
          playCount: totalPlayCountOverall,
          todayCount: campaign.status === 'complete' ? 0 : totalPlayCountToday, // No "today" for completed
          isFinal: campaign.status === 'complete' // Mark as final stats
        };
        
        setCachedData(cacheKey, statsToCache);
        
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchStats();
  }, [campaign]);

  // Assign status class from statuses based on the state of the campaign
  const getStatusClass = (state) => {
    return statuses[state] || 'text-gray-500 bg-gray-200'; // Default to draft-like style if state is unknown
  };

  const getGameTypeColors = (gameTypeId) => {
    const colorMap = {
      "pindrop": {
        bg: "bg-red-100 dark:bg-red-900/60",
        icon: "text-red-500 dark:text-red-400",
      },
      "hint": {
        bg: "bg-amber-100 dark:bg-amber-900/60",
        icon: "text-amber-500 dark:text-amber-400",
      },
      "quiz": {
        bg: "bg-blue-100 dark:bg-blue-900/60",
        icon: "text-blue-500 dark:text-blue-400",
      },
      "mapselect": {
        bg: "bg-emerald-100 dark:bg-emerald-900/60",
        icon: "text-emerald-500 dark:text-emerald-400",
      },
      "snake": {
        bg: "bg-purple-100 dark:bg-purple-900/60",
        icon: "text-purple-500 dark:text-purple-400",
      }
    };
    return colorMap[gameTypeId] || colorMap["quiz"]; // Default to quiz colors
  };

  // Get the gameType data and colors
  const gameTypeData = gameTypes.find((type) => type.id === campaign.gameType);
  const colors = getGameTypeColors(campaign.gameType);

  // Determine if we should show stats
  const showStats = (campaign.status === 'live' || campaign.status === 'complete') && !isLoading;

  return (
    <Link
      to={`/studio/campaign/${campaign.id}`}
      className="block col-span-full sm:col-span-6 xl:col-span-4 studio-card rounded-xl hover:shadow-lg transition-all duration-200"
      style={{
        fontFamily: '"Share Tech Mono", monospace',
        transform: 'perspective(1000px)',
        transition: 'all 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'perspective(1000px) rotateX(1deg) rotateY(1deg) scale(1.01)';
        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.4)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
      }}
    >
      <div className="flex flex-col h-full p-5">
        {/* Header */}
        <header className="flex items-center justify-between mb-3">
          {/* Left: Game Type Icon */}
          <div className="p-2.5 rounded-lg" style={{
            background: 'rgba(241, 196, 15, 0.2)',
            border: '1px solid rgba(241, 196, 15, 0.4)'
          }}>
            {gameTypeData?.icon && (
              <gameTypeData.icon className="w-5 h-5" style={{color: '#F1C40F'}} />
            )}
          </div>

          {/* Right: Formatted Created Date */}
          <div className="text-xs font-bold" style={{color: '#95A5A6'}}>
            {(() => {
              if (!campaign.createdAt) return 'Legacy';
              
              try {
                const date = campaign.createdAt?.toDate ? campaign.createdAt.toDate() : new Date(campaign.createdAt);
                if (isNaN(date.getTime())) return 'Legacy';
                return formatDate(date);
              } catch (error) {
                return 'Legacy';
              }
            })()}
          </div>
        </header>

        {/* Campaign Title and Description */}
        <div className="grow mt-2">
          <h2 className="text-xl leading-snug font-black mb-1 tracking-wide" style={{
            color: '#F1C40F',
            textShadow: '1px 1px 0px #E67E22'
          }}>
            {campaign.name}
          </h2>
          <div className="text-sm font-bold" style={{color: '#ECF0F1'}}>
            {campaign.options.title}
          </div>
        </div>

        {/* Footer: Show Play Count with comparison badge if stats are available, otherwise show status */}
        <footer className="mt-5">
          <div className="flex justify-between items-center">
            {showStats ? (
              <div className="flex items-center">
                <p className="ml-1 text-xs font-black uppercase tracking-wider" style={{color: '#ECF0F1'}}>
                  🎮 PLAYS: {playCount.toLocaleString()}
                </p>
                {renderComparisonBadge(todayCount)}
              </div>
            ) : (
              <span
                className="text-xs inline-flex font-bold rounded-full text-center px-3 py-1"
                style={{
                  background: campaign.status === 'live' 
                    ? 'linear-gradient(135deg, #27AE60, #2ECC71)' 
                    : campaign.status === 'draft'
                    ? 'rgba(52, 73, 94, 0.6)'
                    : 'rgba(231, 76, 60, 0.6)',
                  border: '1px solid #F1C40F',
                  color: '#FFFFFF',
                  textShadow: '1px 1px 0px rgba(0, 0, 0, 0.5)'
                }}
              >
                {campaign.status === 'live' && '🔥 '}
                {campaign.status === 'draft' && '📝 '}
                {campaign.status === 'complete' && '✅ '}
                {campaign.status.toUpperCase()}
              </span>
            )}

            <div className="flex items-center px-3 py-1 rounded transition-all duration-100" style={{
              background: 'rgba(241, 196, 15, 0.2)',
              border: '1px solid rgba(241, 196, 15, 0.4)'
            }}>
              <span className="text-sm font-bold" style={{color: '#F1C40F'}}>👁️ VIEW</span>
              <ArrowRightIcon className="w-4 h-4 ml-1" style={{color: '#F1C40F'}} />
            </div>
          </div>
        </footer>
      </div>
    </Link>
  );
}

export default CampaignsCard;